'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useMemo, useRef, useState, } from 'react';
import { Button } from '../../actions/button/index.js';
import { ErrorPage } from '../../feedback/error-page/index.js';
import { cx } from '../../../internal/cx.js';
import { ShellLink } from '../../../internal/shell/link.js';
import { LockGlyph } from '../../../internal/shell/panel-icons.js';
const DEFAULT_MESSAGES = {
    title: (app) => `You don’t have access to ${app}`,
    description: (app, ws) => ws
        ? `Ask an admin of ${ws} to give you access to ${app}.`
        : `Ask a workspace admin to give you access to ${app}.`,
    requestAccess: 'Request access',
    requested: 'Access requested',
    requestSent: (app) => `Request sent. You can open ${app} once an admin approves it.`,
    requestFailed: 'The request didn’t go through. Try again.',
    contactAdmin: 'Contact an admin',
    home: 'Back to workspace',
    switchWorkspace: 'Switch workspace',
};
function isThenable(v) {
    return !!v && typeof v.then === 'function';
}
/**
 * The "you don't have access to this app" screen: the person is a member of
 * the workspace but has no grant for this app. It explains who can fix it and
 * offers request access, contact an admin, back to the workspace and switch
 * workspace. Built on ErrorPage (`forbidden`). The grant check stays in the app.
 */
export const NoAppAccess = forwardRef(function NoAppAccess({ appName, workspaceName, title, description, onRequestAccess, requestState: requestStateProp, contactHref, homeHref, onSwitchWorkspace, switchWorkspaceHref, workspaceSwitcher, linkAs, fullScreen = false, headingLevel = 'h1', focusOnMount = false, messages: messagesProp, className, ...rest }, ref) {
    const messages = useMemo(() => ({ ...DEFAULT_MESSAGES, ...messagesProp }), [messagesProp]);
    const [innerState, setInnerState] = useState('idle');
    const state = requestStateProp ?? innerState;
    const alive = useRef(true);
    useEffect(() => () => {
        alive.current = false;
    }, []);
    const request = () => {
        if (!onRequestAccess || state === 'pending' || state === 'sent')
            return;
        let result;
        try {
            result = onRequestAccess();
        }
        catch {
            setInnerState('error');
            return;
        }
        if (!isThenable(result)) {
            setInnerState('sent');
            return;
        }
        setInnerState('pending');
        result.then(() => alive.current && setInnerState('sent'), () => alive.current && setInnerState('error'));
    };
    const actions = (_jsxs(_Fragment, { children: [onRequestAccess ? (_jsx(Button, { variant: "primary", loading: state === 'pending', disabled: state === 'sent', onClick: request, children: state === 'sent' ? messages.requested : messages.requestAccess })) : null, homeHref ? (_jsx(Button, { asChild: true, variant: onRequestAccess ? 'secondary' : 'primary', children: _jsx(ShellLink, { as: linkAs, href: homeHref, children: messages.home }) })) : null, workspaceSwitcher ??
                (switchWorkspaceHref ? (_jsx(Button, { asChild: true, variant: "ghost", children: _jsx(ShellLink, { as: linkAs, href: switchWorkspaceHref, children: messages.switchWorkspace }) })) : onSwitchWorkspace ? (_jsx(Button, { variant: "ghost", onClick: onSwitchWorkspace, children: messages.switchWorkspace })) : null)] }));
    const hasActions = Boolean(onRequestAccess || homeHref || workspaceSwitcher || switchWorkspaceHref || onSwitchWorkspace);
    return (_jsxs(ErrorPage, { ref: ref, ...rest, kind: "forbidden", numeral: false, icon: _jsx(LockGlyph, {}), fullScreen: fullScreen, headingLevel: headingLevel, focusOnMount: focusOnMount, className: cx('mn-no-app-access', className), "data-request": state, title: title ?? messages.title(appName), description: description ?? messages.description(appName, workspaceName), actions: hasActions ? actions : undefined, support: contactHref ? (_jsx("a", { className: "mn-no-app-access__contact", href: contactHref, children: messages.contactAdmin })) : undefined, children: [_jsx("p", { className: "mn-no-app-access__status", role: "status", children: state === 'sent' ? messages.requestSent(appName) : null }), state === 'error' ? (_jsx("p", { className: "mn-no-app-access__error", role: "alert", children: messages.requestFailed })) : null] }));
});
//# sourceMappingURL=no-app-access.js.map